/**
 * What the plan step's endpoints return (MAR-1047). The screen shows what billing computed and does
 * no money math of its own (MAR-1039 D6): every amount is integer cents, IVA included where named.
 *
 * NO CARD DATA (D8, AC17): nothing here, and nothing the plan step sends, carries a card number, CVV
 * or expiry. `embedded` means a provider's own hosted fields (MAR-1317, `hostedFields.ts`).
 */

/**
 * The plans the screen offers (D13, plus Plan Redacción, MAR-1186), in display order: ascending tier, the same order as
 * billing's `COMMERCIAL_PLAN_ORDER`. The `rank` helpers (consent's highest-tier plan_id, onboarding's seat order) read
 * the array index, so Redacción, the cheapest seat, stays first.
 */
export const PLAN_SCREEN_PLAN_IDS = ['redaccion', 'light', 'business_starter', 'business_pro', 'business_ultra'] as const
export type PlanScreenPlanId = (typeof PLAN_SCREEN_PLAN_IDS)[number]

export interface SeatPrice {
  netCents: number
  ivaCents: number
  totalCents: number
}

/**
 * One plan as BILLING prices it, on billing's own wire (`GET /v1/prices`): `perSeat` is null while no
 * price is configured ("Precio por confirmar"). Billing's casing; this app's API never returns it.
 */
export interface PlanPrice {
  planId: string
  perSeat: SeatPrice | null
}

/**
 * A plan option on the screen (`GET /api/billing/plans`): billing's price plus the plan's own label.
 * The plan is `plan_id`, its column's name, like every other response here that names a plan
 * (MAR-1273); `planOptions` renames billing's `planId` once, visibly.
 */
export interface PlanOption {
  plan_id: string
  label: string
  perSeat: SeatPrice | null
}

export interface PlanOptionsResult {
  plans: PlanOption[]
  /**
   * Whether a PAID checkout can open for the caller's organization (MAR-1474): billing has a payment provider for
   * its country. False while none is configured (v1.0); the plan step then takes no paid checkout (a voucher that
   * covers the whole total still completes) and the free chat hides its upgrade CTAs.
   */
  checkoutAvailable: boolean
}

export interface OrderQuote {
  subtotalNetCents: number
  discountCents: number
  netCents: number
  ivaCents: number
  totalCents: number
}

/** One tier of the basket (MAR-1404): how many licences of the plan. */
export interface CheckoutLine {
  planId: string
  quantity: number
}

/** `POST /api/billing/quote` (and the checkout's basket): one line per tier, as billing prices it. */
export interface PlanQuoteInput {
  lines: Array<{ planId: PlanScreenPlanId, quantity: number }>
  voucherCode?: string
}

export interface CheckoutInput extends PlanQuoteInput {
  fiscalProfile: FiscalProfileInput
}

/** The fiscal profile the form sends; billing validates and stores it (MAR-1045, MAR-1144). */
export interface FiscalProfileInput {
  razonSocial: string
  nit: string
  nrc?: string | null
  giro?: string | null
  address: string
  billingEmail: string
}

/**
 * El Salvador DTE document mode on `/app/setup` (MAR-1144):
 * - `ccf`: Comprobante de Crédito Fiscal (DTE `03`) — requires `nrc`, `giro`, and `address`.
 * - `factura`: Factura de Consumidor Final (DTE `01`) — `nrc` and `giro` are `null`, `address` is optional.
 */
export type FiscalDocType = 'ccf' | 'factura'

export const FISCAL_PROFILE_FIELDS = ['razonSocial', 'nit', 'nrc', 'giro', 'address', 'billingEmail'] as const
export type FiscalProfileField = (typeof FISCAL_PROFILE_FIELDS)[number]

/** i18n key suffix under `onboarding.plan.fieldErrors.<codeKey>` (MAR-1144 AC2, AC3, AC4). */
export type FiscalProfileFieldErrorCode =
  | 'razonSocial'
  | 'nitCcf'
  | 'nitFactura'
  | 'nrc'
  | 'giro'
  | 'address'
  | 'billingEmail'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const stripSeparators = (value: string) => value.replace(/[\s-]/g, '')

/**
 * Derives the active document selector mode from the profile state:
 * - A blank new profile initializes `nrc: ''` (`!== null`) and therefore defaults to `'ccf'` (AC1).
 * - A saved profile hydrated with `nrc: null` restores `'factura'` (AC1).
 */
export function fiscalDocTypeFor(profile: { nrc?: string | null }): FiscalDocType {
  return profile.nrc === null ? 'factura' : 'ccf'
}

export function validateFiscalProfileField(
  field: FiscalProfileField,
  profile: FiscalProfileInput,
  docType: FiscalDocType = fiscalDocTypeFor(profile),
): FiscalProfileFieldErrorCode | undefined {
  switch (field) {
    case 'razonSocial': {
      const trimmed = profile.razonSocial.trim()
      return !trimmed || trimmed.length > 250 ? 'razonSocial' : undefined
    }
    case 'nit': {
      const digits = stripSeparators(profile.nit)
      const valid = /^\d{9}$|^\d{14}$/.test(digits)
      return valid ? undefined : (docType === 'ccf' ? 'nitCcf' : 'nitFactura')
    }
    case 'nrc': {
      if (docType === 'factura') return undefined
      const digits = stripSeparators(profile.nrc ?? '')
      return /^\d{2,8}$/.test(digits) ? undefined : 'nrc'
    }
    case 'giro': {
      if (docType === 'factura') return undefined
      const trimmed = (profile.giro ?? '').trim()
      return !trimmed || trimmed.length > 250 ? 'giro' : undefined
    }
    case 'address': {
      const trimmed = profile.address.trim()
      if (trimmed.length > 500) return 'address'
      return docType === 'ccf' && !trimmed ? 'address' : undefined
    }
    case 'billingEmail': {
      const trimmed = profile.billingEmail.trim().toLowerCase()
      return !EMAIL_RE.test(trimmed) || trimmed.length > 320 ? 'billingEmail' : undefined
    }
  }
}

export function validateFiscalProfileInput(
  profile: FiscalProfileInput,
  docType: FiscalDocType = fiscalDocTypeFor(profile),
): Partial<Record<FiscalProfileField, FiscalProfileFieldErrorCode>> {
  const errors: Partial<Record<FiscalProfileField, FiscalProfileFieldErrorCode>> = {}
  for (const field of FISCAL_PROFILE_FIELDS) {
    const code = validateFiscalProfileField(field, profile, docType)
    if (code) errors[field] = code
  }
  return errors
}

export function mapServerFiscalFieldErrors(
  fields: unknown,
  docType: FiscalDocType,
): Partial<Record<FiscalProfileField, FiscalProfileFieldErrorCode>> {
  if (!Array.isArray(fields)) return {}
  const mapped: Partial<Record<FiscalProfileField, FiscalProfileFieldErrorCode>> = {}
  for (const item of fields) {
    const rawField = typeof (item as { field?: unknown })?.field === 'string' ? (item as { field: string }).field : ''
    if (rawField === 'razonSocial' || rawField === 'legalName') mapped.razonSocial = 'razonSocial'
    else if (rawField === 'nit') mapped.nit = docType === 'ccf' ? 'nitCcf' : 'nitFactura'
    else if (rawField === 'nrc') mapped.nrc = 'nrc'
    else if (rawField === 'giro') mapped.giro = 'giro'
    else if (rawField === 'address') mapped.address = 'address'
    else if (rawField === 'billingEmail') mapped.billingEmail = 'billingEmail'
  }
  return mapped
}

export interface FiscalFieldDescriptor {
  key: FiscalProfileField
  labelKey: string
  placeholderKey: string
  type: string
  inputmode?: 'numeric' | 'text' | 'email'
  autocomplete: string
  required: boolean
  mono?: boolean
}

export function fiscalFieldDescriptors(docType: FiscalDocType): FiscalFieldDescriptor[] {
  const isCcf = docType === 'ccf'
  const list: FiscalFieldDescriptor[] = [
    {
      key: 'razonSocial',
      labelKey: isCcf ? 'onboarding.plan.razonSocial' : 'onboarding.plan.razonSocialFactura',
      placeholderKey: isCcf ? 'onboarding.plan.placeholders.razonSocialCcf' : 'onboarding.plan.placeholders.razonSocialFactura',
      type: 'text',
      autocomplete: 'organization',
      required: true,
    },
    {
      key: 'nit',
      labelKey: isCcf ? 'onboarding.plan.nitCcf' : 'onboarding.plan.nitFactura',
      placeholderKey: isCcf ? 'onboarding.plan.placeholders.nitCcf' : 'onboarding.plan.placeholders.nitFactura',
      type: 'text',
      inputmode: 'numeric',
      autocomplete: 'off',
      required: true,
      mono: true,
    },
  ]
  if (isCcf) {
    list.push(
      {
        key: 'nrc',
        labelKey: 'onboarding.plan.nrc',
        placeholderKey: 'onboarding.plan.placeholders.nrc',
        type: 'text',
        inputmode: 'numeric',
        autocomplete: 'off',
        required: true,
        mono: true,
      },
      {
        key: 'giro',
        labelKey: 'onboarding.plan.giro',
        placeholderKey: 'onboarding.plan.placeholders.giro',
        type: 'text',
        autocomplete: 'off',
        required: true,
      },
    )
  }
  list.push(
    {
      key: 'address',
      labelKey: isCcf ? 'onboarding.plan.address' : 'onboarding.plan.addressOptional',
      placeholderKey: 'onboarding.plan.placeholders.address',
      type: 'text',
      autocomplete: 'street-address',
      required: isCcf,
    },
    {
      key: 'billingEmail',
      labelKey: 'onboarding.plan.billingEmail',
      placeholderKey: 'onboarding.plan.placeholders.billingEmail',
      type: 'email',
      inputmode: 'email',
      autocomplete: 'email',
      required: true,
    },
  )
  return list
}

/** `GET /api/billing/fiscal-profile`: the stored profile, or null before the first checkout. */
export interface FiscalProfileResult {
  profile: FiscalProfileInput | null
}

/**
 * How checkout ended, as the screen renders it (AC9, AC10):
 *   - `complete`: paid (or zero amount); `seats` licences are bought, all empty (MAR-1399): the founder
 *     assigns them next, in "Asigna tus licencias" (MAR-1404);
 *   - `redirect`: the provider's page, at `url`;
 *   - `embedded`: the provider's hosted fields, mounted in place from `clientConfig` (MAR-1317,
 *     `hostedFields.ts`); `ref` is the checkout to read once the fields are done;
 *   - `unavailable`: no payment provider for the organization's country; nothing was charged.
 */
export type CheckoutOutcome =
  | { kind: 'complete', seats: number }
  | { kind: 'redirect', url: string }
  | { kind: 'embedded', ref: string, clientConfig: Record<string, string> }
  | { kind: 'unavailable' }

export interface CheckoutResult {
  outcome: CheckoutOutcome
  quote: OrderQuote
}
